const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function setupHeadingSwaps(root = document) {
  for (const swap of root.querySelectorAll('[data-bcs-heading-swap]')) {
    if (swap.dataset.bcsHeadingSwapReady === 'true') continue;
    swap.dataset.bcsHeadingSwapReady = 'true';

    const items = [...swap.querySelectorAll('.featured-products__heading-swap-item')];
    if (items.length < 2 || reduceMotionQuery.matches) continue;

    let activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    const interval = Math.max(2000, Number(swap.dataset.swapInterval) || 3000);

    swap.bcsHeadingSwapTimer = window.setInterval(() => {
      const currentItem = items[activeIndex];
      activeIndex = (activeIndex + 1) % items.length;
      const nextItem = items[activeIndex];

      currentItem.classList.remove('is-active');
      currentItem.classList.add('is-leaving');
      nextItem.classList.add('is-active');

      window.setTimeout(() => currentItem.classList.remove('is-leaving'), 450);
    }, interval);
  }
}

function setupCollectionTabs(root = document) {
  for (const tabSection of root.querySelectorAll('[data-bcs-collection-tabs]')) {
    if (tabSection.dataset.bcsCollectionTabsReady === 'true') continue;
    tabSection.dataset.bcsCollectionTabsReady = 'true';

    const tabs = [...tabSection.querySelectorAll('[data-bcs-collection-tab]')];
    const panels = [...tabSection.querySelectorAll('[data-bcs-collection-panel]')];
    const dynamicCollectionLink = tabSection.querySelector('[data-bcs-active-collection-link]');
    if (!tabs.length || !panels.length) continue;

    const activateTab = (nextTab, focusTab = false) => {
      const panelId = nextTab.getAttribute('aria-controls');

      for (const tab of tabs) {
        const isActive = tab === nextTab;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      }

      for (const panel of panels) {
        const isActive = panel.id === panelId;
        panel.hidden = !isActive;
        if (isActive) panel.querySelector('[data-bcs-scroll-track]')?.scrollTo({ left: 0, behavior: 'auto' });
      }

      if (dynamicCollectionLink && nextTab.dataset.collectionUrl) {
        dynamicCollectionLink.href = nextTab.dataset.collectionUrl;
      }
      if (focusTab) nextTab.focus();
    };

    tabSection.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const tab = event.target.closest('[data-bcs-collection-tab]');
      if (tab) activateTab(tab);
    });

    tabSection.addEventListener('keydown', (event) => {
      if (!(event.target instanceof Element)) return;
      const currentTab = event.target.closest('[data-bcs-collection-tab]');
      if (!currentTab) return;

      const currentIndex = tabs.indexOf(currentTab);
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === currentIndex) return;

      event.preventDefault();
      activateTab(tabs[nextIndex], true);
    });

    activateTab(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
  }
}

function setupUgcSliders(root = document) {
  if (typeof window.Swiper !== 'function') return;

  for (const component of root.querySelectorAll('[data-bcs-ugc-slider]')) {
    if (component.dataset.bcsUgcReady === 'true') continue;
    component.dataset.bcsUgcReady = 'true';

    const slider = component.querySelector('[data-bcs-swiper]');
    const previousButton = component.querySelector('[data-bcs-ugc-prev]');
    const nextButton = component.querySelector('[data-bcs-ugc-next]');
    if (!slider) continue;
    const slides = [...slider.querySelectorAll('[data-bcs-ugc-slide]')];
    if (!slides.length) continue;
    const videos = [...slider.querySelectorAll('video')];
    const originalSlideCount = new Set(slides.map((slide) => slide.dataset.bcsUgcIndex)).size;
    const middleCopySlides = slides.filter((slide) => slide.dataset.bcsUgcCopy === '2');
    const requestedInitialIndex = Number(component.dataset.bcsUgcInitialIndex);
    const middleSlideIndex = Math.floor(slides.length / 2);
    const initialOriginalIndex = Number.isInteger(requestedInitialIndex) ? requestedInitialIndex : 0;
    const matchingInitialSlides = middleCopySlides
      .map((slide) => ({ index: slides.indexOf(slide), originalIndex: Number(slide.dataset.bcsUgcIndex) }))
      .filter((slide) => slide.originalIndex === initialOriginalIndex);
    const initialSlide = matchingInitialSlides.length
      ? matchingInitialSlides.reduce((closest, slide) =>
          Math.abs(slide.index - middleSlideIndex) < Math.abs(closest.index - middleSlideIndex)
            ? slide
            : closest
        ).index
      : middleSlideIndex;

    const getActiveSlide = (swiper) =>
      swiper.slides?.[swiper.activeIndex] || slider.querySelector('.swiper-slide-active');

    const preloadPriority = { none: 0, metadata: 1, auto: 2 };
    const prepareVideo = (video, preload) => {
      const currentPreload = video.dataset.bcsUgcPreload || video.preload || 'none';
      if (preloadPriority[currentPreload] >= preloadPriority[preload]) return;

      video.preload = preload;
      video.dataset.bcsUgcPreload = preload;
    };

    const syncActiveSlide = (swiper, settled = false) => {
      const activeSlide = getActiveSlide(swiper);
      const swiperSlides = [...swiper.slides];
      const activeIndex = swiperSlides.indexOf(activeSlide);
      const previousSlide = swiperSlides[swiper.previousIndex];

      for (const slide of slides) {
        const isActive = slide === activeSlide;
        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-ui-active', isActive || (!settled && slide === previousSlide));
        slide.setAttribute('aria-current', isActive ? 'true' : 'false');
      }

      for (const video of videos) {
        const videoSlide = video.closest('[data-bcs-ugc-slide]');
        const videoIndex = swiperSlides.indexOf(videoSlide);
        const directDistance = Math.abs(videoIndex - activeIndex);
        const loopDistance = Math.min(directDistance, swiperSlides.length - directDistance);
        const isActiveVideo = videoSlide === activeSlide;
        const preload = isActiveVideo ? 'auto' : loopDistance === 1 ? 'metadata' : 'none';

        if (!isActiveVideo && !video.paused) video.pause();
        prepareVideo(video, preload);
      }
    };

    const recenterToMiddleCopy = (swiper) => {
      if (originalSlideCount < 2) return false;
      const activeSlide = getActiveSlide(swiper);
      if (activeSlide?.dataset.bcsUgcCopy === '2') return false;

      const originalIndex = activeSlide?.dataset.bcsUgcIndex;
      const middleSlide = middleCopySlides.find((slide) => slide.dataset.bcsUgcIndex === originalIndex);
      const targetIndex = middleSlide ? [...swiper.slides].indexOf(middleSlide) : -1;
      if (targetIndex < 0) return false;

      slider.classList.add('bcs-video-slider--recentering');
      swiper.slideTo(targetIndex, 0, false);
      return true;
    };

    let initializationFrame;
    let readyFrame;
    let recenterFrame;
    let recenterReadyFrame;
    const finishInitialization = () => {
      window.cancelAnimationFrame(initializationFrame);
      window.cancelAnimationFrame(readyFrame);
      initializationFrame = window.requestAnimationFrame(() => {
        readyFrame = window.requestAnimationFrame(() => {
          slider.classList.remove('bcs-video-slider--initializing');
        });
      });
    };

    const finishRecentering = () => {
      window.cancelAnimationFrame(recenterFrame);
      window.cancelAnimationFrame(recenterReadyFrame);
      recenterFrame = window.requestAnimationFrame(() => {
        recenterReadyFrame = window.requestAnimationFrame(() => {
          slider.classList.remove('bcs-video-slider--recentering');
        });
      });
    };

    slider.classList.add('bcs-video-slider--initializing');

    const swiper = new window.Swiper(slider, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      centeredSlides: true,
      loop: false,
      initialSlide,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      followFinger: true,
      threshold: 2,
      touchAngle: 55,
      touchRatio: 1,
      touchStartPreventDefault: false,
      focusableElements: 'input, select, option, textarea, button, label',
      shortSwipes: true,
      longSwipes: true,
      longSwipesMs: 300,
      longSwipesRatio: 0.12,
      resistanceRatio: 0.85,
      observer: true,
      observeParents: true,
      updateOnWindowResize: true,
      resizeObserver: true,
      watchSlidesProgress: true,
      speed: reduceMotionQuery.matches ? 0 : 420,
      autoplay: false,
      preventClicks: true,
      preventClicksPropagation: true,
      preventInteractionOnTransition: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: {
        nextEl: nextButton,
        prevEl: previousButton,
      },
      breakpoints: {
        750: {
          spaceBetween: 20,
        },
      },
      on: {
        beforeResize() {
          slider.classList.add('bcs-video-slider--initializing');
        },
        init(swiper) {
          syncActiveSlide(swiper, true);
          finishInitialization();
        },
        slideChange(swiper) {
          syncActiveSlide(swiper);
        },
        transitionEnd(swiper) {
          const wasRecentered = recenterToMiddleCopy(swiper);
          syncActiveSlide(swiper, true);
          if (wasRecentered) finishRecentering();
        },
        resize(swiper) {
          syncActiveSlide(swiper, true);
          finishInitialization();
        },
      },
    });
    component.bcsSwiper = swiper;
    component.bcsUgcCleanup = () => {
      window.cancelAnimationFrame(initializationFrame);
      window.cancelAnimationFrame(readyFrame);
      window.cancelAnimationFrame(recenterFrame);
      window.cancelAnimationFrame(recenterReadyFrame);
      slider.classList.remove('bcs-video-slider--initializing');
      slider.classList.remove('bcs-video-slider--recentering');
    };
    component.bcsSlideToOriginal = (originalIndex, speed = swiper.params.speed) => {
      const targetSlide = middleCopySlides.find(
        (slide) => Number(slide.dataset.bcsUgcIndex) === Number(originalIndex)
      );
      const targetIndex = targetSlide ? [...swiper.slides].indexOf(targetSlide) : -1;
      if (targetIndex >= 0) swiper.slideTo(targetIndex, speed);
    };
    slider.bcsSwiper = swiper;

    let pointerStart;
    slider.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) return;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });
    slider.addEventListener('pointercancel', () => {
      pointerStart = undefined;
    });
    slider.addEventListener('pointerup', (event) => {
      const start = pointerStart;
      pointerStart = undefined;
      if (!start || start.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) return;
      if (!(event.target instanceof Element) || event.target.closest('a, button')) return;

      const tappedSlide = event.target.closest('[data-bcs-ugc-slide]');
      if (tappedSlide !== getActiveSlide(swiper)) return;

      const activeVideo = tappedSlide.querySelector('video');
      if (activeVideo && !activeVideo.paused) activeVideo.pause();
    });

    for (const video of videos) {
      const slide = video.closest('[data-bcs-ugc-slide]');
      const playButton = slide?.querySelector('[data-bcs-ugc-play]');
      const syncSoundState = () => {
        const hasSound = !video.muted && video.volume > 0;
        slide?.classList.toggle('has-sound', hasSound);
        playButton?.setAttribute('aria-label', hasSound ? 'Pause video' : 'Play video with sound');
      };
      const toggleVideo = () => {
        if (!video.paused) {
          video.pause();
          return;
        }
        video.muted = false;
        video.volume = 1;
        if (video.ended) video.currentTime = 0;
        video.play().catch(() => {});
      };
      playButton?.addEventListener('click', toggleVideo);
      video.addEventListener('play', () => {
        for (const otherVideo of slider.querySelectorAll('video')) {
          if (otherVideo !== video) otherVideo.pause();
        }
        slide?.classList.add('is-playing');
        syncSoundState();
      });
      const stopPlaying = () => {
        slide?.classList.remove('is-playing');
        slide?.classList.remove('has-sound');
        playButton?.setAttribute('aria-label', 'Play video');
      };
      video.addEventListener('pause', stopPlaying);
      video.addEventListener('ended', stopPlaying);
      video.addEventListener('volumechange', syncSoundState);
    }
  }
}

function setupCommunityReviewSliders(root = document) {
  if (typeof window.Swiper !== 'function') return;

  for (const component of root.querySelectorAll('[data-bcs-community-reviews]')) {
    if (component.dataset.bcsCommunityReviewsReady === 'true') continue;
    component.dataset.bcsCommunityReviewsReady = 'true';

    const slider = component.querySelector('[data-bcs-community-swiper]');
    if (!slider) continue;

    const swiper = new window.Swiper(slider, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      speed: reduceMotionQuery.matches ? 0 : 420,
      grabCursor: true,
      watchOverflow: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: {
        prevEl: component.querySelector('[data-bcs-community-prev]'),
        nextEl: component.querySelector('[data-bcs-community-next]'),
      },
      breakpoints: {
        990: {
          slidesPerView: 4,
          spaceBetween: 16,
        },
      },
    });

    component.bcsSwiper = swiper;
    slider.bcsSwiper = swiper;
  }
}

function setupEditorialResultSliders(root = document) {
  if (typeof window.Swiper !== 'function') return;

  for (const component of root.querySelectorAll('[data-bcs-editorial-results]')) {
    if (component.dataset.bcsEditorialResultsReady === 'true') continue;

    const slider = component.querySelector('[data-bcs-editorial-results-swiper]');
    const slides = slider ? [...slider.querySelectorAll('.editorial-features__result-slide')] : [];
    if (!slider || !slides.length) continue;

    component.dataset.bcsEditorialResultsReady = 'true';
    const controls = component.querySelector('.editorial-features__results-controls');
    const swiper = new window.Swiper(slider, {
      slidesPerView: 1.14,
      slidesPerGroup: 1,
      spaceBetween: 14,
      speed: reduceMotionQuery.matches ? 0 : 420,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      touchStartPreventDefault: false,
      watchOverflow: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: {
        prevEl: component.querySelector('[data-bcs-editorial-results-prev]'),
        nextEl: component.querySelector('[data-bcs-editorial-results-next]'),
      },
      pagination: {
        el: component.querySelector('[data-bcs-editorial-results-pagination]'),
        clickable: true,
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
          spaceBetween: 18,
        },
        990: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
      },
    });

    const syncControlsVisibility = () => {
      if (controls) controls.hidden = swiper.isLocked;
    };
    swiper.on('lock', syncControlsVisibility);
    swiper.on('unlock', syncControlsVisibility);
    swiper.on('breakpoint', syncControlsVisibility);
    syncControlsVisibility();

    component.bcsSwiper = swiper;
    slider.bcsSwiper = swiper;
  }
}

function setupEditorialComparisons(root = document) {
  for (const comparison of root.querySelectorAll('[data-bcs-before-after]')) {
    if (comparison.dataset.bcsBeforeAfterReady === 'true') continue;
    comparison.dataset.bcsBeforeAfterReady = 'true';

    const range = comparison.querySelector('[data-bcs-before-after-range]');
    if (!range) continue;
    const updatePosition = () => {
      comparison.style.setProperty('--bcs-before-after-position', `${range.value}%`);
    };
    range.addEventListener('input', updatePosition);
    range.addEventListener('change', updatePosition);
    updatePosition();
  }
}

function setupCommunityJourneySliders(root = document) {
  for (const slider of root.querySelectorAll('[data-community-journey-slider]')) {
    if (slider.dataset.bcsCommunityJourneyReady === 'true') continue;
    slider.dataset.bcsCommunityJourneyReady = 'true';

    const slides = [...slider.querySelectorAll('[data-community-journey-slide]')];
    const dots = [...slider.querySelectorAll('[data-community-journey-dot]')];
    if (slides.length < 2) continue;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true'));
    const showSlide = (nextIndex) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-hidden', !isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
      dots.forEach((dot) => {
        const isActive = Number(dot.dataset.communityJourneyDot) === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });
    };

    slider.querySelector('[data-community-journey-prev]')?.addEventListener('click', () => showSlide(activeIndex - 1));
    slider.querySelector('[data-community-journey-next]')?.addEventListener('click', () => showSlide(activeIndex + 1));
    dots.forEach((dot) =>
      dot.addEventListener('click', () => showSlide(Number(dot.dataset.communityJourneyDot))),
    );
    slider.bcsShowCommunityJourneySlide = showSlide;
  }
}

function setupHome(root = document) {
  setupHeadingSwaps(root);
  setupCollectionTabs(root);
  setupUgcSliders(root);
  setupCommunityReviewSliders(root);
  setupEditorialResultSliders(root);
  setupEditorialComparisons(root);
  setupCommunityJourneySliders(root);

  for (const track of root.querySelectorAll('[data-bcs-scroll-track]')) {
    if (track.dataset.bcsReady === 'true') continue;
    track.dataset.bcsReady = 'true';
    if (track.matches('[data-bcs-ugc-track]')) continue;
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const direction = event.key === 'ArrowRight' ? 1 : -1;

      if (track.matches('.quick-categories__grid, .bcs-routine__grid')) {
        const items = [...track.children];
        if (!items.length) return;
        const trackLeft = track.getBoundingClientRect().left;
        const currentIndex = items.reduce((closestIndex, item, index) => {
          const itemLeft = track.scrollLeft + item.getBoundingClientRect().left - trackLeft;
          const closestLeft =
            track.scrollLeft + items[closestIndex].getBoundingClientRect().left - trackLeft;
          return Math.abs(itemLeft - track.scrollLeft) < Math.abs(closestLeft - track.scrollLeft)
            ? index
            : closestIndex;
        }, 0);
        const targetIndex = Math.min(Math.max(currentIndex + direction, 0), items.length - 1);
        const targetLeft = track.scrollLeft + items[targetIndex].getBoundingClientRect().left - trackLeft;

        event.preventDefault();
        track.scrollTo({ left: targetLeft, behavior: reduceMotionQuery.matches ? 'auto' : 'smooth' });
        return;
      }

      track.scrollBy({
        left: track.clientWidth * 0.8 * direction,
        behavior: reduceMotionQuery.matches ? 'auto' : 'smooth',
      });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupHome(), { once: true });
} else {
  setupHome();
}
if (document.readyState !== 'complete') {
  window.addEventListener('load', () => setupHome(), { once: true });
}
document.addEventListener('shopify:section:load', (event) => setupHome(event.target));
document.addEventListener('shopify:block:select', (event) => {
  if (!(event.target instanceof Element)) return;
  event.target.closest('[data-bcs-collection-tab]')?.click();
  const ugcSlide = event.target.closest('[data-bcs-ugc-slide]');
  const ugcComponent = ugcSlide?.closest('[data-bcs-ugc-slider]');
  if (ugcSlide && ugcComponent) ugcComponent.bcsSlideToOriginal?.(Number(ugcSlide.dataset.bcsUgcIndex));
  const communitySlide = event.target.closest('.community-reviews__slide');
  const communitySwiper = communitySlide?.closest('[data-bcs-community-swiper]')?.bcsSwiper;
  if (communitySlide && communitySwiper) communitySwiper.slideTo([...communitySwiper.slides].indexOf(communitySlide));
  const resultSlide = event.target.closest('.editorial-features__result-slide');
  const resultSwiper = resultSlide?.closest('[data-bcs-editorial-results-swiper]')?.bcsSwiper;
  if (resultSlide && resultSwiper) {
    resultSwiper.slideTo([...resultSwiper.slides].indexOf(resultSlide));
  }
  const journeySlide = event.target.closest('[data-community-journey-slide]');
  const journeySlider = journeySlide?.closest('[data-community-journey-slider]');
  if (journeySlide && journeySlider) {
    journeySlider.bcsShowCommunityJourneySlide?.(
      [...journeySlider.querySelectorAll('[data-community-journey-slide]')].indexOf(journeySlide),
    );
  }
});
document.addEventListener('shopify:section:unload', (event) => {
  for (const swap of event.target.querySelectorAll('[data-bcs-heading-swap]')) {
    window.clearInterval(swap.bcsHeadingSwapTimer);
  }
  for (const slider of event.target.querySelectorAll('[data-bcs-ugc-slider]')) {
    slider.bcsUgcCleanup?.();
    slider.bcsSwiper?.destroy(true, true);
  }
  for (const slider of event.target.querySelectorAll('[data-bcs-community-reviews]')) {
    slider.bcsSwiper?.destroy(true, true);
  }
  for (const slider of event.target.querySelectorAll('[data-bcs-editorial-results]')) {
    slider.bcsSwiper?.destroy(true, true);
  }
});
